import { LoadingState } from '@lcs/shared/api';
import { EntityAdapter } from '@ngrx/entity';
import { on, ReducerTypes } from '@ngrx/store';
import { CallDirectionTypes, OrderingDirections, OrderingOption, Providers } from 'generated/graphql-types';
import { RegisterAdapter } from 'src/app/common-components/register/models/register-state.adapter';
import { ActiveCallsRegisterOptions } from '../models/active-calls-register-options';
import { ActiveCallsRegisterListItem } from '../models/active-calls-register-results.model';
import { SharedCallRegisterState } from './shared-register-state.model';

export function createCallRegisterReducer<T extends SharedCallRegisterState>(
   adapter: RegisterAdapter<ActiveCallsRegisterListItem, any, OrderingOption>,
   entityAdapter: EntityAdapter<ActiveCallsRegisterListItem>,
   adapterActions: any,
   initialState: T,
   ...ons: ReducerTypes<T, any>[]
) {
   return adapter.createReducer(
      adapterActions,
      initialState,
      on(adapterActions.pageEnter, (state) => ({
         ...state,
         callState: LoadingState.Loading,
      })),
      on(adapterActions.loadSuccess, adapterActions.loadPageSuccess, (state, action) => ({
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
      })),
      on(adapterActions.quickFilterChanged, (state, action) => ({
         ...state,
         callState: LoadingState.Loading,
         quickFilters: action.quickFilters,
      })),
      on(adapterActions.orderingOptionsChanged, (state, action) => ({
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((opt) => !action.orderingOptions.some((newOpt) => newOpt.field === opt.field))
                    .filter((opt) => opt.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions.filter((opt) => opt.direction !== OrderingDirections.NotSet),
              ]
            : action.orderingOptions.filter((opt) => opt.direction !== OrderingDirections.NotSet),
      })),
      on(adapterActions.clearRegisterFilters, (state) => ({
         ...state,
         quickFilters: initialState.quickFilters,
         isAutoRefresh: initialState.isAutoRefresh,
         orderingOptions: initialState.orderingOptions,
      })),
      ...ons
   );
}

/**
 * Converts a SignalR action payload into a register list item.
 */
export function convertActionToActiveRegisterListItem(action: { callDetail: any }): ActiveCallsRegisterListItem {
   const providerId = getProvider(action.callDetail.providerId);
   const sourceProviderId = getProvider(action.callDetail.sourceProviderId);
   const destinationProviderId = getProvider(action.callDetail.destinationProviderId);

   return {
      accountName: action.callDetail.account?.name ?? null,
      callDestination:
         action.callDetail.callDirectionTypeId === 2
            ? action.callDetail.destinationNumber ?? ''
            : action.callDetail.destinationExtension
            ? `${action.callDetail.destinationExtension.number} - ${action.callDetail.destinationExtension.name}`
            : action.callDetail.destinationNumber ?? '',
      callDetailId: action.callDetail.callDetailId,
      callDirection: action.callDetail?.callDirectionTypeId
         ? CallDirectionTypes[CallDirectionTypesConverter[action.callDetail.callDirectionTypeId]]
         : null,
      callDispositionTypeId: action.callDetail.callDispositionTypeId,
      callRate: action.callDetail.callRate,
      callSource:
         action.callDetail.callDirectionTypeId === 1
            ? action.callDetail.sourceNumber ?? ''
            : action.callDetail.sourceExtension
            ? `${action.callDetail.sourceExtension.number} - ${action.callDetail.sourceExtension.name}`
            : action.callDetail.sourceNumber ?? '',
      destinationExtension: action.callDetail.destinationExtension,
      destinationNumber: action.callDetail.destinationNumber,
      originalDestinationNumber: action.callDetail.originalDestinationNumber,
      phoneNumber: action.callDetail.phoneNumber?.phoneNumber,
      startTime: action.callDetail.startTime,
      callRecordingId: action.callDetail.callRecordingId,
      callerChannelId: action.callDetail.callerChannelId,
      serverName: action.callDetail.serverName,
      sipCallId: action.callDetail.sipCallId,
      providerId,
      sourceProviderId,
      destinationProviderId,
      callRecordingFile: action.callDetail.callRecordingId,
      isPhoneCallThatWentDirectlyToSourceExtension: action.callDetail.isPhoneCallThatWentDirectlyToSourceExtension,
   };
}

export function matchesProviderFilter(callDetail: any, quickFilters: ActiveCallsRegisterOptions | null): boolean {
   if (!quickFilters?.providerId) {
      return true;
   }

   const providerId = getProvider(callDetail.providerId);
   const sourceProviderId = getProvider(callDetail.sourceProviderId);
   const destinationProviderId = getProvider(callDetail.destinationProviderId);

   return (
      sourceProviderId === quickFilters.providerId ||
      destinationProviderId === quickFilters.providerId ||
      (!sourceProviderId && !destinationProviderId && providerId === quickFilters.providerId)
   );
}

function getProvider(providerId: Providers | number | string | null | undefined): Providers | null {
   if (providerId == null) {
      return null;
   }

   if (typeof providerId === 'number') {
      return Providers[CallProviderTypesConverter[providerId] as keyof typeof Providers] ?? null;
   }

   return Providers[providerId as keyof typeof Providers] ?? null;
}

export function multiFieldSort(
   orderingOptions: OrderingOption[],
   list: ActiveCallsRegisterListItem[]
): ActiveCallsRegisterListItem[] {
   // NotSet means "no sort on this field" - keep the server's order rather than treating it as Desc.
   orderingOptions = orderingOptions.filter((opt) => opt.direction !== OrderingDirections.NotSet);
   if (!orderingOptions.length) return list;

   const compare = (a: any, b: any) => (a < b ? -1 : a > b ? 1 : 0);

   const tagged = list.map((item, index) => ({ item, index }));

   tagged.sort((a, b) => {
      for (const opt of orderingOptions) {
         let result = 0;
         switch (opt.field) {
            case 'startTime':
               result = compare(a.item.startTime, b.item.startTime);
               break;
            case 'sourceNumber':
               result = compare(a.item.callSource, b.item.callSource);
               break;
            case 'destinationNumber':
               result = compare(a.item.destinationNumber, b.item.destinationNumber);
               break;
            case 'callDirectionTypeId':
               result = compare(a.item.callDirection, b.item.callDirection);
               break;
            case 'account.name':
               result = compare(a.item.accountName, b.item.accountName);
               break;
            case 'sourceProviderId':
               result = compare(a.item.sourceProviderId, b.item.sourceProviderId);
               break;
            case 'destinationProviderId':
               result = compare(a.item.destinationProviderId, b.item.destinationProviderId);
               break;
            default:
               result = 0;
         }
         if (result !== 0) {
            return opt.direction === 'Asc' ? result : -result;
         }
      }
      return a.index - b.index;
   });

   return tagged.map((t) => t.item);
}

export enum CallDirectionTypesConverter {
   Inbound = 1,
   Outbound = 2,
   Internal = 3,
   System = 4,
}

export enum CallProviderTypesConverter {
   Inteliquent = 1,
   Lumen = 2,
   TimeWarner = 3,
   Twilio = 4,
   NDT = 5,
}
